/**
 * Мелочи, общие для экранов расписания.
 *
 * Отдельным файлом, потому что читателей много (календарь, карточка занятия,
 * окно создания, список серий, редактор серии, экраны группы), а вторая копия
 * названий дней недели это ровно то место, где однажды разъедется нумерация.
 *
 * ⚠️ С 17.09.2026 двое читателей живут ВНЕ расписания: список уроков и редактор
 * заготовки зовут отсюда `defaultStart`, потому что окно «Новое занятие» у всех
 * троих одно. Правило «здесь только мелочи расписания» от этого стало строже, а
 * не слабее: заводимое тут обязано быть верным и для того, кто в сетку не
 * заходил вовсе.
 */
import {
  BOOKING_BATCH_MAX,
  GROUP_MEMBERS_MAX,
  addLocalDays,
  epochDayOf,
  isoWeekdayOf,
  localDateAt,
  localDateOfEpochDay,
  localMinutesAt,
  type OccurrenceLifecycle,
  type ScheduleEntry,
  type ScheduleWarning,
} from "@edu/shared";
import * as api from "../../lib/api.ts";
import { dayText } from "../../lib/when.ts";
import { lessonErrorText } from "../lessons/lesson-errors.ts";
import { MEMBERS_HIDDEN } from "./group-text.ts";

/**
 * Названия дней недели в ISO-порядке: индекс 0 это понедельник, то есть
 * `weekday = 1`.
 *
 * ⚠️ `Date.prototype.getDay()` считает иначе (0 это воскресенье), и это ровно
 * то место, где ошибаются. Здесь порядок ISO и только ISO, тот же, что у
 * `EXTRACT(ISODOW FROM …)` и у колонки `schedule_slot.weekday`.
 */
export const WEEKDAYS = [
  "понедельник",
  "вторник",
  "среда",
  "четверг",
  "пятница",
  "суббота",
  "воскресенье",
] as const;

/** Короткое имя дня для строки серии: «вт», «чт». */
export const WEEKDAYS_SHORT = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"] as const;

export function weekdayName(weekday: number): string {
  return WEEKDAYS[weekday - 1] ?? String(weekday);
}

export function weekdayShort(weekday: number): string {
  return WEEKDAYS_SHORT[weekday - 1] ?? String(weekday);
}

/**
 * Догадка браузера о зоне.
 *
 * Нужна ровно до подтверждения пояса: без неё расписание пришлось бы не
 * показывать вовсе, потому что молчаливого UTC на сервере нет ни в одной ветке.
 * Записать её сама она не может: подтверждение это отдельное нажатие человека,
 * а догадка ошибается на VPN, на чужом ноутбуке и в командировке.
 *
 * ⚠️ Реализация переехала в `lib/time-zone.ts` 20.08.2026: копий было две,
 * здесь и в каркасе кабинета, слово в слово. Реэкспорт оставлен затем, чтобы
 * два экрана расписания не переучивались на новый адрес заодно с правкой,
 * которая их не касается.
 */
export { guessTimeZone } from "../../lib/time-zone.ts";

/**
 * Куда встаёт новое занятие, пока человек не поправил поля: сегодня, ближайший
 * круглый час.
 *
 * ⚠️ Живёт здесь, а не у экрана, с 17.09.2026. Зовут её двое, список уроков и
 * кнопка в редакторе заготовки: у сетки расписания время берётся из места
 * нажатия, а этим двоим нажимать не на что. Вторая редакция правила означала бы,
 * что одна и та же кнопка на соседних экранах предлагает разное время.
 *
 * ⚠️ Считается в момент НАЖАТИЯ, а не при отрисовке экрана. Вкладка кабинета
 * живёт открытой часами, и «ближайший час», посчитанный при загрузке, к вечеру
 * предлагал бы утро того же дня, то есть прошлое.
 *
 * ⚠️ Час берётся от местной полуночи в зоне смотрящего, а не из `Date`. У части
 * поясов смещение с получасом, и круглый час там не совпадает с круглым часом
 * браузера; а после 23:00 ближайший круглый час это уже завтрашняя полночь,
 * поэтому день считается вместе с часом, а не отдельно от него.
 */
export function defaultStart(zone: string): { date: string; time: string } {
  const now = Date.now();
  const today = localDateAt(now, zone);
  const hour = Math.floor(localMinutesAt(now, zone) / 60) + 1;
  return {
    date: hour === 24 ? addLocalDays(today, 1) : today,
    time: `${String(hour % 24).padStart(2, "0")}:00`,
  };
}

/**
 * Отказы расписания словами.
 *
 * Кодов немного, и каждый означает конкретную вещь, которую человек может
 * поправить. Оставлять их машинными («slot_overlap») значит заставлять читать
 * английский там, где вся остальная страница по-русски.
 *
 * Ключи это коды из общего словаря, а не свободные строки: переименование кода
 * на сервере обязано ронять сборку клиента здесь, а не тихо уводить человека в
 * ветку по умолчанию с английским словом на экране.
 *
 * ⚠️ **Трёх отказов создания урока здесь нет намеренно**, и дописывать их сюда
 * не надо: их текст собирает `lessonErrorText`, потому что в статическую строку
 * не влезают номера слайдов из тела отказа. Разбор целиком в `errorText` ниже.
 */
const ERROR_TEXTS: Readonly<Partial<Record<api.ApiErrorCode, string>>> = {
  slot_overlap: "Этот день и время накладываются на другой день той же серии.",
  occurrence_taken: "На это время у серии уже стоит занятие. Выберите другое.",
  occurrence_cancelled: "Занятие отменено.",
  occurrence_not_scheduled: "Расписание такого занятия не производит.",
  lesson_started: "Занятие уже состоялось: его нельзя ни отменить, ни перенести.",
  lesson_exists: "К занятию уже подготовлен урок.",
  lesson_materialized:
    "Доску этого урока уже открывали, и материал заготовки в неё разложен. " +
    "Замена заготовки его не изменит: поправьте слайды прямо в уроке либо уберите " +
    "урок в корзину и подготовьте заново.",
  student_not_linked: "Пара отвязана: новых занятий по ней не завести.",
  move_too_far: "Слишком далеко: перенос дальше чем на 180 дней теряет занятие из виду.",
  /*
    ⚠️ Слова без «серии» и без «расписания группы» намеренно: код общий, а
    отвечают им обе ручки переноса, и у группы серии нет вовсе, у неё
    расписание. «Повторения» это то единственное, что верно про обе.
  */
  move_after_series_end: "Занятие туда не переедет: повторения заканчиваются раньше этой даты.",
  entry_not_open: "Войти пока рано: ученику дверь открывается за пять минут до начала.",
  entry_closed: "Окно входа закрылось.",
  bad_time_zone: "Такого часового пояса нет.",
  invalid_range: "Дата окончания раньше даты начала.",
  too_many_slots: "Слишком много дней в одной серии.",
  occurrence_managed: "Это занятие серии: двигать его надо переносом, а не как разовое.",
  plan_not_found: "Заготовка не найдена: её могли переименовать или убрать в корзину.",
  window_too_wide: "Слишком широкое окно расписания.",
  /*
    Самозапись (`docs/booking-design.md`). ⚠️ `opening_taken` один на все
    причины отказа записи, и слова у него одни: различать «занял другой»,
    «закрыли» и «у преподавателя занятие» ученику нельзя (1.5 проекта).
  */
  opening_taken: "Это время уже недоступно.",
  opening_booked: "На это время уже записался ученик: занятие стоит в расписании.",
  too_many_openings: `За один раз можно открыть не больше ${String(BOOKING_BATCH_MAX)} недель. Выберите день поближе.`,
  series_not_found:
    "Расписания больше нет: его могли распустить в другой вкладке. Откройте страницу заново.",

  /*
    Отказы расписания ГРУППЫ. Экраны проверяют то же самое ДО нажатия и
    показывают причину прямо в списке групп (`group-text.ts`), поэтому сюда
    человек попадает в одном случае: пока он заполнял форму, состав или
    расписание группы поменялись из другой вкладки. Слова взяты оттуда же, а не
    придуманы заново: предпроверка и отказ обязаны говорить одно и то же, иначе
    человек решит, что это разные беды.
  */
  group_archived: "Группа в архиве: расписания ей не завести.",
  group_schedule_exists:
    "У этой группы уже есть расписание. Новый день добавляется в нём, а не вторым расписанием.",
  group_schedule_empty: "Ни у кого в группе нет действующей ссылки, а без неё занятий не назначить.",
  group_schedule_too_big:
    `В группе больше учеников, чем берёт расписание: завести его можно не больше чем на ${GROUP_MEMBERS_MAX}.`,
  /*
    ⚠️ Глагол ОБЩИЙ, а не перечень действий. Перечня здесь и стоял («отменять и
    переносить»), и он устарел дважды подряд, не сломав ни одного теста: тем же
    кодом отвечают правка занятия и, с 02.09.2026, правка ссылки на созвон
    (`routes/schedule/external-call.ts`). Читатель пришёл сюда за ответом «почему
    не вышло», а не за списком того, что бывает.
  */
  group_schedule_managed:
    "Это занятие группы: менять его надо сразу у всех, а не по одному ученику.",
  group_schedule_live: "У группы есть действующее расписание: сначала распустите его.",
  group_room_unavailable: "У занятия группы нашей комнаты пока нет: оно идёт по внешней ссылке.",
  /*
    ⚠️ Слова те же, что у текста в карточке занятия, и это не совпадение:
    предпроверка и отказ обязаны говорить одно и то же. Читает этот отказ и
    ученик, поэтому «откройте состав» тут нет: сделать это может только
    преподаватель, и путь ему показан на его карточке.
  */
  group_members_hidden: MEMBERS_HIDDEN,
};

export function errorText(e: unknown): string {
  if (!(e instanceof api.ApiError)) return e instanceof Error ? e.message : String(e);

  /*
    ⚠️ **Отказы создания урока в карту выше НЕ кладутся**, и причин две.

    Первая: номера слайдов с недособранным заданием приезжают в ТЕЛЕ отказа
    (`details.exercises`), и строкой в статической карте они не выражаются
    вовсе. Человек получил бы «в заготовке есть задание без правильного ответа»
    и ни одной подсказки, где его искать на шестидесяти слайдах.

    Вторая: `lesson-errors.ts` заведён ровно затем, чтобы текст был один и тот
    же во всех местах, где создаётся урок, а таких мест с 17.09.2026 три.
    Вторая редакция тех же слов здесь означала бы два разных продукта на
    соседних экранах, ради чего тот модуль и появился.

    ⚠️ Место отказа названо `occurrence` жёстко, и это законно ровно потому, что
    из расписания эти коды приходят от ОДНОЙ ручки, `POST /v1/schedule/lessons`,
    то есть от кнопки «Завести занятие». «Подготовить»
    (`POST /v1/schedule/occurrences/:slotId/:occursOn/lesson`) упражнений не
    компилирует вовсе. Появись компиляция и там, зачин начнёт врать: занятие
    уже есть, и не заводится именно урок.

    ⚠️ `student_not_linked` сюда НЕ входит намеренно: в карте выше у него свои
    слова, про отвязанную ПАРУ, и на экране расписания они уместнее.
  */
  if (
    e.code === "exercise_key_missing" ||
    e.code === "invalid_exercise_source" ||
    e.code === "exercise_kind_unsupported"
  ) {
    return lessonErrorText(e, "occurrence");
  }

  // Запасной путь остаётся: сюда доходят отказы чужих разделов и отказы вне
  // словаря вроде ошибки разбора у самого Fastify
  return ERROR_TEXTS[e.code] ?? e.message;
}

/* ───────────────────────── подписи и форматирование ──────────────────────── */

/**
 * Календарная дата словами: «3 мар».
 *
 * ⚠️ Строка разбирается по частям, а не отдаётся в `new Date("2026-03-03")`:
 * такую форму движок читает как UTC, и западнее Гринвича дата съезжает на сутки
 * назад ещё до того, как её напечатают. Здесь дата без времени, и поменяться она
 * не имеет права ни в одном поясе.
 *
 * Год добавляется только когда он не текущий, и это правило одно на весь
 * кабинет: им заведует `dayText` из `lib/when.ts`.
 *
 * ⚠️ Живёт здесь, а не у экрана, с 30.08.2026: копий было две (список серий и
 * экран расписания группы), и стояли они на соседних экранах, между которыми
 * человек ходит кнопкой. Две редакции одной даты он бы увидел.
 */
export function localDateText(date: string, now: number): string {
  const [y, m, d] = date.split("-").map(Number);
  // Разбор не удался: показываем то, что пришло, а не «Invalid Date»
  if (y === undefined || m === undefined || d === undefined) return date;
  return dayText(new Date(y, m - 1, d).getTime(), now);
}

/**
 * Подписи состояний. Ровно те, что в терминологии макета, плюс три новых
 * («отменено», «перенесено», «не состоялось»), которых у макета не было.
 *
 * ⚠️ Все строчными: подпись живёт ровно в одном месте, в пилюле состояния, а
 * пилюли набираются строчными во всём кабинете. С прописной она читалась
 * началом предложения и спорила с именем ученика, стоящим в той же строке.
 *
 * `detached` это не состояние занятия, а его отсутствие: урок остался, а
 * занятие из расписания ушло. У разового занятия состояние обычное, и в этот
 * ключ оно не попадает никогда.
 *
 * ⚠️ **«Прошло» и «Не состоялось» это разные утверждения, и путать их нельзя.**
 * «Не состоялось» говорит, что вход был открыт и никто не вошёл. У занятия без
 * нашей комнаты (сегодня это занятие группы) входа нет вовсе, шло оно по
 * внешней ссылке, и что там было, мы не знаем: сказать про него «не
 * состоялось» значит соврать преподавателю, ученику и родителю разом. Правило
 * считает сервер (`lifecycleOf`), здесь оно только подписывается.
 */
export const STATE_LABEL: Record<OccurrenceLifecycle | "detached", string> = {
  scheduled: "запланировано",
  open: "можно начать",
  ready: "урок готов",
  live: "идёт",
  done: "завершено",
  cancelled: "отменено",
  moved_away: "перенесено",
  missed: "не состоялось",
  past: "прошло",
  detached: "вне расписания",
};

/**
 * Тон пилюли под каждое состояние.
 *
 * ⚠️ Цветом отмечено только то, что требует действия прямо сейчас, и покрашены
 * два состояния из десяти. Восемь разноцветных пилюль это ёлка, в которой не
 * видно идущего занятия, и до 14.09.2026 в ней было три цвета: «можно начать» и
 * «урок готов» носили индиговый. Индиго снят потому, что в системе это цвет
 * главного действия и текущего места, и состояние, покрашенное им, спорит с
 * кнопкой «Войти», стоящей в той же карточке.
 *
 * Выключенный тон достаётся отменённому и перенесённому, то есть ровно тем
 * двум, из-за которых гаснет и сама карточка: занятия не будет, и плашка обязана
 * уходить на задний план вместе с ним.
 */
export const STATE_TONE: Record<
  OccurrenceLifecycle | "detached",
  "ok" | "warn" | "off" | undefined
> = {
  scheduled: undefined,
  open: undefined,
  ready: undefined,
  live: "ok",
  done: undefined,
  cancelled: "off",
  moved_away: "off",
  missed: "warn",
  past: undefined,
  detached: undefined,
};

/**
 * Понедельник недели, в которую попадает дата.
 *
 * Считается по номеру суток, а не по `Date.getDay()`: тот считает воскресенье
 * нулём, и это ровно то место, где ошибаются.
 */
export function mondayOf(date: string): string {
  const day = epochDayOf(date);
  return localDateOfEpochDay(day - (isoWeekdayOf(date) - 1));
}

const dayFmt = new Intl.DateTimeFormat("ru-RU", { weekday: "long", day: "numeric", month: "long" });
const dayShortFmt = new Intl.DateTimeFormat("ru-RU", { weekday: "short", day: "numeric" });
const rangeFmt = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });

/**
 * Подпись дня.
 *
 * ⚠️ Дата собирается как полдень UTC, а не как местная полночь: форматтер
 * работает в зоне браузера, и полночь в ней уехала бы на предыдущие сутки у
 * всех, кто западнее Гринвича. Сама дата пришла уже посчитанной в зоне
 * смотрящего, переводить её ещё раз нечем и незачем.
 */
export function dayTitle(date: string): string {
  return dayFmt.format(new Date(`${date}T12:00:00Z`));
}

/** Короткая подпись колонки календаря: «пн 18». Полдень UTC по той же причине. */
export function dayShort(date: string): string {
  return dayShortFmt.format(new Date(`${date}T12:00:00Z`));
}

export function rangeTitle(from: string, to: string): string {
  if (from === to) return rangeFmt.format(new Date(`${from}T12:00:00Z`));
  return `${rangeFmt.format(new Date(`${from}T12:00:00Z`))} по ${rangeFmt.format(new Date(`${to}T12:00:00Z`))}`;
}

/** Время занятия в зоне смотрящего. Форматтер на зону, а не на вызов. */
const timeFormatters = new Map<string, Intl.DateTimeFormat>();
export function timeText(at: number, zone: string): string {
  let f = timeFormatters.get(zone);
  if (f === undefined) {
    f = new Intl.DateTimeFormat("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone: zone,
    });
    timeFormatters.set(zone, f);
  }
  return f.format(new Date(at));
}

/** `ЧЧ:ММ` из минут местных суток. Своё, а не через `Intl`: зоны тут уже нет. */
export function minutesToTime(minutes: number): string {
  const m = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export function occurrenceCountText(n: number): string {
  const ten = n % 10;
  const hundred = n % 100;
  if (ten === 1 && hundred !== 11) return `${String(n)} занятие`;
  if (ten >= 2 && ten <= 4 && (hundred < 12 || hundred > 14)) return `${String(n)} занятия`;
  return `${String(n)} занятий`;
}

/** Первая буква имени. Пустое имя даёт точку, а не пустой квадрат. */
export function firstLetter(name: string | null): string {
  return (name ?? "").trim().slice(0, 1).toUpperCase() || "·";
}

/**
 * Особый местный час словами. Пустая строка у обычного времени.
 *
 * ⚠️ Живёт здесь, а не строкой в разметке, с 10.09.2026: читателей стало двое,
 * карточка занятия (`EntryNotes`) и окно у места падения при переносе
 * перетаскиванием (`DropToast`). Вторая копия этой фразы означала бы, что про
 * один и тот же двойной час два экрана говорят разными словами.
 */
export function markNote(mark: ScheduleEntry["mark"]): string {
  if (mark === "gap") return "этого времени в этот день не существует, занятие сдвинуто вперёд";
  if (mark === "ambiguous") return "этот час в этот день идёт дважды, взят ранний";
  return "";
}

/**
 * Наложение с другим занятием словами.
 *
 * ⚠️ Предупреждения приезжают ТОЛЬКО преподавателю, и решает это сервер: поля
 * `warnings` в ответе ученику нет вовсе. Роль здесь не проверяется специально,
 * чтобы не завести второе место, где это правило можно забыть.
 */
export function overlapNote(w: ScheduleWarning, zone: string): string {
  const what =
    w.code === "student_overlap"
      ? "у этого ученика в это же время другое занятие"
      : "у вас в это же время другое занятие";
  return `${what}${w.withName !== null ? `: ${w.withName}` : ""}, ${timeText(w.withAt, zone)}`;
}
